import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";

// Add new languages here as they become available. `rtl: true` flips layout.
export const LANGUAGES = [
  { code: "de", label: "Deutsch", rtl: false },
  { code: "en", label: "English", rtl: false },
  { code: "tr", label: "Türkçe", rtl: false },
  { code: "ar", label: "العربية", rtl: true },
  { code: "ro", label: "Română", rtl: false },
  { code: "fa", label: "فارسی / دری", rtl: true },
  { code: "ps", label: "پښتو", rtl: true },
  { code: "uk", label: "Українська", rtl: false },
  { code: "pl", label: "Polski", rtl: false },
  { code: "bg", label: "Български", rtl: false },
  { code: "it", label: "Italiano", rtl: false },
  { code: "es", label: "Español", rtl: false },
  { code: "fr", label: "Français", rtl: false },
];

const RTL_CODES = LANGUAGES.filter((l) => l.rtl).map((l) => l.code);
const STORAGE_KEY = "esvan_lang";
const DEFAULT_LANG = "de";

const I18nContext = createContext(null);

function resolveKey(obj, path) {
  return path.split(".").reduce((acc, part) => (acc && acc[part] != null ? acc[part] : undefined), obj);
}

const SUPPORTED = LANGUAGES.map((l) => l.code);

function detectInitialLang() {
  // A manual choice (persisted) always wins and is never overridden by auto-detect.
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED.includes(saved)) return saved;
  // First visit: use the browser language if it is one of the 13 supported; else DE.
  const candidates = [navigator.language, ...(navigator.languages || [])];
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split("-")[0];
    if (SUPPORTED.includes(base)) return base;
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLang);

  const dir = RTL_CODES.includes(lang) ? "rtl" : "ltr";

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
  }, [lang, dir]);

  const t = useCallback(
    (key, fallback) => {
      const active = resolveKey(translations[lang], key);
      if (active != null) return active;
      if (process.env.NODE_ENV === "development" && lang !== DEFAULT_LANG) {
        // Controlled fallback: report missing key in dev only (no sensitive data).
        console.warn(`[i18n] missing key "${key}" for lang "${lang}" — falling back to ${DEFAULT_LANG}`);
      }
      const base = resolveKey(translations[DEFAULT_LANG], key);
      return base != null ? base : fallback != null ? fallback : key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, dir, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
